<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'

/**
 * CardHeaderTags Component
 *
 * Displays organization name and tags in the header section of a card.
 * Features:
 * - Always displays organization name if provided
 * - Intelligently shows as many tags as can fit in the available space
 * - When there are too many tags to fit, displays one tag at a time with a fade animation
 * - Automatically recalculates visible tags on resize or when tags change
 */
const props = defineProps<{
  /** The name of the organization to display */
  organizationName?: string
  /** Tags to display, each with text and optional icon */
  headerTags?: { text: string, icon?: string }[]
}>()

const tagsWrapper = ref<HTMLElement | null>(null)
const currentTagIndex = ref(0)
const animationInterval = ref<NodeJS.Timeout | null>(null)
const maxTagsToShow = ref(1) // Start with 1, will be updated based on accurate measurements

/**
 * Computed property that determines which tags should be visible
 * - Shows all tags if they fit in the available space
 * - Otherwise shows just the current tag in the rotation
 */
const visibleTags = computed(() => {
  if (!props.headerTags?.length) return []

  // If we have fewer tags than max, show them all
  if (props.headerTags.length <= maxTagsToShow.value) {
    return props.headerTags
  }

  // For fade effect, just show one tag at a time
  return [props.headerTags[currentTagIndex.value]]
})

/**
 * Determines if tags need to be rotated (animated)
 * True when there are more tags than can fit in the available space
 */
const shouldRotate = computed(() => {
  return (props.headerTags?.length || 0) > maxTagsToShow.value
})

onMounted(() => {
  // Start with a short delay to ensure DOM is ready
  setTimeout(() => {
    calculateMaxTagsAccurately()
    startAnimationIfNeeded()
  }, 200)

  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  stopAnimation()
})

// Watch for changes in the tags
watch(() => props.headerTags, () => {
  nextTick(() => {
    calculateMaxTagsAccurately()
    startAnimationIfNeeded()
  })
}, { deep: true })

/**
 * Handles window resize events by recalculating visible tags
 * and updating the animation state
 */
function handleResize() {
  calculateMaxTagsAccurately()
  startAnimationIfNeeded()
}

/**
 * Accurately calculates how many tags can fit in the available space
 * by measuring the actual width of each tag's content.
 *
 * Process:
 * 1. Creates a temporary, invisible element with the same styling as tags
 * 2. Measures each tag's actual rendered width
 * 3. Accounts for icons and spacing
 * 4. Calculates exact number of tags that can fit
 * 5. Updates animation state based on this calculation
 */
function calculateMaxTagsAccurately() {
  if (!tagsWrapper.value || !props.headerTags?.length) return

  const containerWidth = tagsWrapper.value.clientWidth
  const availableWidth = containerWidth

  // Create a temporary div to measure tag widths accurately
  const tempDiv = document.createElement('div')
  tempDiv.style.position = 'absolute'
  tempDiv.style.visibility = 'hidden'
  tempDiv.style.whiteSpace = 'nowrap'
  tempDiv.style.display = 'inline-block'
  tempDiv.className = 'rounded-2xl bg-white px-3 py-2 text-xs'
  document.body.appendChild(tempDiv)

  // Measure each tag's width individually
  const tagWidths = props.headerTags.map((tag) => {
    // Add icon space if needed
    const iconSpace = tag.icon ? 20 : 0
    tempDiv.textContent = tag.text
    return tempDiv.offsetWidth + iconSpace + 8 // Adding gap
  })

  // Remove the temp element
  document.body.removeChild(tempDiv)

  // Calculate how many tags can fit
  let currentWidth = 0
  let fittingTags = 0

  for (let width of tagWidths) {
    currentWidth += width
    if (currentWidth <= availableWidth) {
      fittingTags++
    }
    else {
      break
    }
  }

  // Update max tags to show (at least 1)
  maxTagsToShow.value = Math.max(1, fittingTags)

  // Reset animation if we can show all tags
  if (maxTagsToShow.value >= props.headerTags.length) {
    stopAnimation()
    currentTagIndex.value = 0
  }
}

/**
 * Starts the tag rotation animation if needed.
 * Animation cycles through tags with a fade effect every 5 seconds.
 */
function startAnimationIfNeeded() {
  if (shouldRotate.value) {
    if (animationInterval.value) {
      clearInterval(animationInterval.value)
    }

    animationInterval.value = setInterval(() => {
      if (!props.headerTags?.length) return

      // Move to next tag
      currentTagIndex.value = (currentTagIndex.value + 1) % props.headerTags.length
    }, 5000) // Rotate every 5 seconds - slower rotation for less distraction
  }
  else {
    stopAnimation()
  }
}

/**
 * Stops the animation interval when no longer needed
 */
function stopAnimation() {
  if (animationInterval.value) {
    clearInterval(animationInterval.value)
    animationInterval.value = null
  }
}
</script>

<template>
  <div v-if="organizationName || headerTags?.length" class="absolute bottom-2 left-2 flex gap-2 pr-2">
    <!-- Organization name - always visible, meaning no fade animation -->
    <div
      v-if="organizationName"
      class="shrink-0 rounded-2xl bg-white px-3 py-2 text-xs text-black"
    >
      <Icon name="ph:buildings" />
      {{ organizationName }}
    </div>

    <!-- Tags container with fade effect for overflow -->
    <div ref="tagsWrapper" class="relative flex h-[34px] max-w-[calc(100%-4rem)] gap-2">
      <TransitionGroup
        name="tag-transition"
        tag="div"
        class="relative flex gap-2"
      >
        <div
          v-for="tag in visibleTags"
          :key="tag.text"
          class="whitespace-nowrap rounded-2xl bg-white px-3 py-2 text-xs text-black"
        >
          <Icon v-if="tag.icon" :name="tag.icon" />
          {{ tag.text }}
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<style scoped>
/*
  Transition styles for the tag fade effect:
  - Uses pure opacity transition (no movement)
  - Tags are absolutely positioned to fade in/out in the same spot
  - Slightly longer 0.7s transition for smoother fades
*/
.tag-transition-enter-active,
.tag-transition-leave-active {
  transition: opacity 0.7s ease;
  position: absolute;
}
.tag-transition-enter-from,
.tag-transition-leave-to {
  opacity: 0;
}

/* Position the tags to overlay in the same space */
.tag-transition-move {
  transition: all 0.5s ease;
}
</style>
